import React, { useState, useEffect } from 'react';
import './SelectiveQuestionBank.css';
import { supabase } from '../../supabaseClient';

/**
 * SelectiveQuestionBank - Admin interface to view, add, and edit Selective Exam questions
 */
const SelectiveQuestionBank = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [examTypes, setExamTypes] = useState([]);
  const [selectedExamType, setSelectedExamType] = useState('');
  const [filters, setFilters] = useState({
    section: 'all',
    domain: 'all',
    difficulty: 'all',
    searchQuery: ''
  });
  const [sections, setSections] = useState([]);
  const [domains, setDomains] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    bySection: {},
    byDifficulty: {}
  });
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateConfig, setGenerateConfig] = useState({
    exam_type_id: '',
    section_id: '',
    domain_id: '',
    difficulty_level: 'foundation',
    question_count: 10
  });
  const [uploadingDiagram, setUploadingDiagram] = useState(false);

  const API_BASE = process.env.REACT_APP_BACKEND_URL || 'https://mathcampus.vercel.app';

  // All /api/selective/admin endpoints require an admin Bearer token
  const authHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session ? { 'Authorization': `Bearer ${session.access_token}` } : {};
  };

  // Fetch all data on mount
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Fetch questions when filters or exam type change
  useEffect(() => {
    if (sections.length > 0) {
      fetchQuestions();
    }
  }, [filters, selectedExamType]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);

      // Fetch exam types
      const examTypesRes = await fetch(`${API_BASE}/api/selective/admin/exam-types`, {
        headers: await authHeaders()
      });

      if (!examTypesRes.ok) {
        throw new Error('Failed to load exam types');
      }

      const examTypesData = await examTypesRes.json();
      setExamTypes(examTypesData);

      // Auto-select first exam type if available
      if (examTypesData.length > 0) {
        setSelectedExamType(examTypesData[0].id);
        await loadSections(examTypesData[0].id);
      }

      // Load all domains initially (no filter)
      await loadDomains();

      // Now fetch questions
      await fetchQuestions();

    } catch (err) {
      console.error('Error loading initial data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadSections = async (examTypeId) => {
    try {
      const url = examTypeId
        ? `${API_BASE}/api/selective/admin/sections?exam_type_id=${examTypeId}`
        : `${API_BASE}/api/selective/admin/sections`;

      const sectionsRes = await fetch(url, { headers: await authHeaders() });

      if (!sectionsRes.ok) {
        throw new Error('Failed to load sections');
      }

      const sectionsData = await sectionsRes.json();
      setSections(sectionsData);

      // Reset section filter when exam type changes
      setFilters(prev => ({ ...prev, section: 'all', domain: 'all' }));

    } catch (err) {
      console.error('Error loading sections:', err);
    }
  };

  const loadDomains = async (sectionId = null) => {
    try {
      const url = sectionId
        ? `${API_BASE}/api/selective/admin/domains?section_id=${sectionId}`
        : `${API_BASE}/api/selective/admin/domains`;

      const domainsRes = await fetch(url, { headers: await authHeaders() });

      if (!domainsRes.ok) {
        throw new Error('Failed to load domains');
      }

      const domainsData = await domainsRes.json();
      setDomains(domainsData);

      // Reset domain filter when section changes
      if (sectionId) {
        setFilters(prev => ({ ...prev, domain: 'all' }));
      }

    } catch (err) {
      console.error('Error loading domains:', err);
    }
  };

  const fetchQuestions = async () => {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (filters.section !== 'all') params.append('section_id', filters.section);
      if (filters.domain !== 'all') params.append('domain_id', filters.domain);
      if (filters.difficulty !== 'all') params.append('difficulty_level', filters.difficulty);
      if (filters.searchQuery) params.append('search', filters.searchQuery);
      params.append('limit', '500'); // Get more questions to allow frontend filtering

      const response = await fetch(`${API_BASE}/api/selective/admin/questions?${params}`, {
        headers: await authHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to fetch questions');
      }

      const data = await response.json();
      let questionsData = data.questions || [];

      // Filter by selected exam type if one is selected
      if (selectedExamType) {
        questionsData = questionsData.filter(q => q.exam_type_id === selectedExamType);
      }

      setQuestions(questionsData);

      // Calculate stats
      calculateStats(questionsData);

    } catch (err) {
      console.error('Error fetching questions:', err);
      setError(err.message);
    }
  };

  const calculateStats = (questionsData) => {
    const stats = {
      total: questionsData.length,
      bySection: {},
      byDifficulty: {}
    };

    questionsData.forEach(q => {
      // Count by section
      const sectionName = q.section_name || 'Unknown';
      stats.bySection[sectionName] = (stats.bySection[sectionName] || 0) + 1;

      // Count by difficulty
      const difficulty = q.difficulty_level || 'unknown';
      stats.byDifficulty[difficulty] = (stats.byDifficulty[difficulty] || 0) + 1;
    });

    setStats(stats);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));

    // When section changes, reload domains filtered by that section
    if (filterName === 'section') {
      if (value === 'all') {
        loadDomains(); // Load all domains
      } else {
        loadDomains(value); // Load domains for selected section
      }
    }
  };

  const handleQuestionClick = (question) => {
    setSelectedQuestion(question);
    setIsEditing(false);
  };

  const handleEditQuestion = () => {
    setIsEditing(true);
  };

  const handleSaveQuestion = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/selective/admin/questions/${selectedQuestion.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify(selectedQuestion)
      });

      if (!response.ok) {
        throw new Error('Failed to save question');
      }

      // Refresh questions list
      await fetchQuestions();
      setIsEditing(false);
      alert('Question updated successfully!');

    } catch (err) {
      console.error('Error saving question:', err);
      alert('Failed to save question: ' + err.message);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/selective/admin/questions/${questionId}`, {
        method: 'DELETE',
        headers: await authHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to delete question');
      }

      // Refresh questions list
      await fetchQuestions();
      setSelectedQuestion(null);
      alert('Question deleted successfully!');

    } catch (err) {
      console.error('Error deleting question:', err);
      alert('Failed to delete question: ' + err.message);
    }
  };

  const handleOpenGenerateModal = () => {
    setShowGenerateModal(true);
    // Pre-fill with current filter values if set
    setGenerateConfig(prev => ({
      ...prev,
      section_id: filters.section !== 'all' ? filters.section : '',
      domain_id: filters.domain !== 'all' ? filters.domain : ''
    }));
  };

  const handleGenerateQuestions = async () => {
    // Validate inputs
    if (!generateConfig.section_id) {
      alert('Please select a section');
      return;
    }
    if (!generateConfig.domain_id) {
      alert('Please select a domain');
      return;
    }

    try {
      setGenerating(true);

      // Get Supabase session token for authentication
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('Not authenticated. Please log in again.');
      }

      const response = await fetch(`${API_BASE}/api/selective/admin/generate-questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify(generateConfig)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to generate questions');
      }

      const result = await response.json();

      alert(`Successfully generated ${result.questions_created || generateConfig.question_count} questions!`);

      // Close modal and refresh questions
      setShowGenerateModal(false);
      await fetchQuestions();

    } catch (err) {
      console.error('Error generating questions:', err);
      alert('Failed to generate questions: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  // Diagram upload handler
  const handleDiagramUpload = async (e, questionId) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a PNG, JPEG, or SVG image file.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB.');
      return;
    }

    // Prompt for alt text (required)
    const altText = window.prompt('Enter alt text for the diagram (required for accessibility):');
    if (!altText || altText.trim().length === 0) {
      alert('Alt text is required for accessibility compliance.');
      return;
    }

    // Prompt for caption (optional)
    const caption = window.prompt('Enter caption for the diagram (optional, press OK to skip):');

    try {
      setUploadingDiagram(true);

      // Get Supabase session token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated. Please log in again.');
      }

      // Create form data
      const formData = new FormData();
      formData.append('file', file);
      formData.append('alt_text', altText.trim());
      if (caption && caption.trim().length > 0) {
        formData.append('caption', caption.trim());
      }

      const response = await fetch(`${API_BASE}/api/selective/admin/questions/${questionId}/diagram/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to upload diagram');
      }

      const result = await response.json();
      alert('Diagram uploaded successfully!');

      // Refresh questions to show the new diagram
      await fetchQuestions();

      // If this question is currently selected, update it
      if (selectedQuestion?.id === questionId) {
        const updatedQuestion = questions.find(q => q.id === questionId);
        if (updatedQuestion) {
          setSelectedQuestion(updatedQuestion);
        }
      }

    } catch (err) {
      console.error('Error uploading diagram:', err);
      alert('Failed to upload diagram: ' + err.message);
    } finally {
      setUploadingDiagram(false);
      // Reset file input
      e.target.value = '';
    }
  };

  // Diagram delete handler
  const handleRemoveDiagram = async (questionId) => {
    if (!window.confirm('Are you sure you want to remove this diagram? This action cannot be undone.')) {
      return;
    }

    try {
      setUploadingDiagram(true);

      // Get Supabase session token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated. Please log in again.');
      }

      const response = await fetch(`${API_BASE}/api/selective/admin/questions/${questionId}/diagram`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to remove diagram');
      }

      alert('Diagram removed successfully!');

      // Refresh questions
      await fetchQuestions();

      // If this question is currently selected, update it
      if (selectedQuestion?.id === questionId) {
        const updatedQuestion = questions.find(q => q.id === questionId);
        if (updatedQuestion) {
          setSelectedQuestion(updatedQuestion);
        }
      }

    } catch (err) {
      console.error('Error removing diagram:', err);
      alert('Failed to remove diagram: ' + err.message);
    } finally {
      setUploadingDiagram(false);
    }
  };

  if (loading) {
    return (
      <div className="question-bank-loading">
        <div className="spinner"></div>
        <p>Loading question bank...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="question-bank-error">
        <h3>⚠️ Error Loading Questions</h3>
        <p>{error}</p>
        <button onClick={fetchInitialData}>Try Again</button>
      </div>
    );
  }

  return (
    <div className="selective-question-bank">
      <header className="bank-header">
        <div>
          <h1>📚 Selective Exam Question Bank</h1>
          <p className="subtitle">View and manage {stats.total} AI-generated questions</p>
        </div>
        <button onClick={handleOpenGenerateModal} className="btn-generate">
          ✨ Generate Questions
        </button>
      </header>

      {/* Stats Overview */}
      <section className="stats-section">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total Questions</div>
        </div>
        {Object.entries(stats.bySection).map(([section, count]) => (
          <div key={section} className="stat-card">
            <div className="stat-value">{count}</div>
            <div className="stat-label">{section}</div>
          </div>
        ))}
      </section>

      {/* Filters */}
      <section className="filters-section">
        <div className="filter-group">
          <label>Exam Type:</label>
          <select
            value={selectedExamType}
            onChange={(e) => {
              const examTypeId = e.target.value;
              setSelectedExamType(examTypeId);
              if (examTypeId) {
                loadSections(examTypeId);
              }
            }}
          >
            <option value="">All Exam Types</option>
            {examTypes.map(et => (
              <option key={et.id} value={et.id}>{et.name} - {et.target_year_level}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Section:</label>
          <select
            value={filters.section}
            onChange={(e) => handleFilterChange('section', e.target.value)}
          >
            <option value="all">All Sections</option>
            {sections.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Domain:</label>
          <select
            value={filters.domain}
            onChange={(e) => handleFilterChange('domain', e.target.value)}
          >
            <option value="all">All Domains</option>
            {domains.map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Difficulty:</label>
          <select
            value={filters.difficulty}
            onChange={(e) => handleFilterChange('difficulty', e.target.value)}
          >
            <option value="all">All Levels</option>
            <option value="foundation">Foundation</option>
            <option value="developing">Developing</option>
            <option value="proficient">Proficient</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>

        <div className="filter-group search-group">
          <label>Search:</label>
          <input
            type="text"
            placeholder="Search questions..."
            value={filters.searchQuery}
            onChange={(e) => handleFilterChange('searchQuery', e.target.value)}
          />
        </div>
      </section>

      {/* Main Content: Questions List + Detail View */}
      <div className="bank-content">
        {/* Questions List */}
        <aside className="questions-list">
          <h3>{questions.length} Questions</h3>
          <div className="list-container">
            {questions.map((q, idx) => (
              <div
                key={q.id}
                className={`question-item ${selectedQuestion?.id === q.id ? 'selected' : ''}`}
                onClick={() => handleQuestionClick(q)}
              >
                <div className="question-number">Q{idx + 1}</div>
                <div className="question-preview">
                  {q.question_text.substring(0, 80)}...
                </div>
                <div className="question-meta">
                  <span className={`difficulty ${q.difficulty_level}`}>
                    {q.difficulty_level}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </aside>

        {/* Question Detail View */}
        <main className="question-detail">
          {selectedQuestion ? (
            <div className="detail-container">
              <div className="detail-header">
                <h2>Question Details</h2>
                <div className="detail-actions">
                  {!isEditing && (
                    <>
                      <button onClick={handleEditQuestion} className="btn-edit">
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => handleDeleteQuestion(selectedQuestion.id)}
                        className="btn-delete"
                      >
                        🗑️ Delete
                      </button>
                    </>
                  )}
                  {isEditing && (
                    <>
                      <button onClick={handleSaveQuestion} className="btn-save">
                        💾 Save
                      </button>
                      <button
                        onClick={() => setIsEditing(false)}
                        className="btn-cancel"
                      >
                        ❌ Cancel
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="detail-body">
                {/* Reading Passage (if present) */}
                {selectedQuestion.passage_text && (
                  <div className="field-group passage-section">
                    <label>📚 Reading Passage:</label>
                    {selectedQuestion.passage_title && (
                      <div className="passage-title-display">
                        <strong>{selectedQuestion.passage_title}</strong>
                      </div>
                    )}
                    <div className="passage-display">
                      {selectedQuestion.passage_text}
                    </div>
                  </div>
                )}

                {/* Question Text */}
                <div className="field-group">
                  <label>Question:</label>
                  {isEditing ? (
                    <textarea
                      value={selectedQuestion.question_text}
                      onChange={(e) =>
                        setSelectedQuestion(prev => ({
                          ...prev,
                          question_text: e.target.value
                        }))
                      }
                      rows={4}
                    />
                  ) : (
                    <p className="question-text">{selectedQuestion.question_text}</p>
                  )}
                </div>

                {/* Options */}
                <div className="field-group">
                  <label>Options:</label>
                  <div className="options-list">
                    {selectedQuestion.options?.map((opt, idx) => (
                      <div key={idx} className={`option ${opt.is_correct ? 'correct' : ''}`}>
                        <span className="option-letter">{opt.option_letter}.</span>
                        <span className="option-text">{opt.option_text}</span>
                        {opt.is_correct && <span className="correct-badge">✓ Correct</span>}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Explanation */}
                {selectedQuestion.options?.find(opt => opt.is_correct)?.explanation && (
                  <div className="field-group explanation-section">
                    <label>📖 Explanation:</label>
                    <div className="explanation-text">
                      {selectedQuestion.options.find(opt => opt.is_correct).explanation}
                    </div>
                  </div>
                )}

                {/* Metadata */}
                <div className="metadata-grid">
                  <div className="meta-item">
                    <label>Section:</label>
                    <span>{selectedQuestion.section_name}</span>
                  </div>
                  <div className="meta-item">
                    <label>Domain:</label>
                    <span>{selectedQuestion.domain_name}</span>
                  </div>
                  <div className="meta-item">
                    <label>Difficulty:</label>
                    <span className={`badge ${selectedQuestion.difficulty_level}`}>
                      {selectedQuestion.difficulty_level}
                    </span>
                  </div>
                  <div className="meta-item">
                    <label>AI Generated:</label>
                    <span>{selectedQuestion.ai_generated ? 'Yes' : 'No'}</span>
                  </div>
                </div>

                {/* Diagram Management */}
                <div className="diagram-section">
                  <h3>📊 Question Diagram</h3>

                  {selectedQuestion.diagram_url ? (
                    <div className="diagram-preview">
                      <img
                        src={selectedQuestion.diagram_url}
                        alt={selectedQuestion.diagram_alt_text || "Question diagram"}
                        className="diagram-preview-image"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          console.error('Failed to load diagram:', selectedQuestion.diagram_url);
                        }}
                      />
                      <div className="diagram-info">
                        <p><strong>Alt Text:</strong> {selectedQuestion.diagram_alt_text}</p>
                        {selectedQuestion.diagram_caption && (
                          <p><strong>Caption:</strong> {selectedQuestion.diagram_caption}</p>
                        )}
                      </div>
                      <button
                        onClick={() => handleRemoveDiagram(selectedQuestion.id)}
                        disabled={uploadingDiagram}
                        className="btn-danger"
                      >
                        {uploadingDiagram ? 'Removing...' : '🗑️ Remove Diagram'}
                      </button>
                    </div>
                  ) : (
                    <div className="diagram-upload">
                      <p>No diagram attached to this question.</p>
                      <label className="btn-upload">
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                          onChange={(e) => handleDiagramUpload(e, selectedQuestion.id)}
                          disabled={uploadingDiagram}
                          style={{ display: 'none' }}
                        />
                        {uploadingDiagram ? 'Uploading...' : '📤 Upload Diagram'}
                      </label>
                      <p className="upload-hint">Supported: PNG, JPEG, SVG (max 5MB, 2000x2000px)</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="no-selection">
              <p>← Select a question from the list to view details</p>
            </div>
          )}
        </main>
      </div>

      {/* Generate Questions Modal */}
      {showGenerateModal && (
        <div className="modal-overlay" onClick={() => !generating && setShowGenerateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>✨ Generate New Questions</h2>
              <button
                className="close-button"
                onClick={() => setShowGenerateModal(false)}
                disabled={generating}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Exam Type: *</label>
                <select
                  value={generateConfig.exam_type_id}
                  onChange={(e) => {
                    const examTypeId = e.target.value;
                    setGenerateConfig(prev => ({ ...prev, exam_type_id: examTypeId, section_id: '', domain_id: '' }));
                    // Load sections for this exam type
                    if (examTypeId) {
                      loadSections(examTypeId);
                    }
                  }}
                  disabled={generating}
                >
                  <option value="">Select an exam type...</option>
                  {examTypes.map(et => (
                    <option key={et.id} value={et.id}>{et.name} - {et.target_year_level}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Section: *</label>
                <select
                  value={generateConfig.section_id}
                  onChange={(e) => {
                    setGenerateConfig(prev => ({ ...prev, section_id: e.target.value, domain_id: '' }));
                    // Load domains for this section
                    if (e.target.value) {
                      loadDomains(e.target.value);
                    }
                  }}
                  disabled={generating || !generateConfig.exam_type_id}
                >
                  <option value="">Select a section...</option>
                  {sections.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Domain: *</label>
                <select
                  value={generateConfig.domain_id}
                  onChange={(e) => setGenerateConfig(prev => ({ ...prev, domain_id: e.target.value }))}
                  disabled={generating || !generateConfig.section_id}
                >
                  <option value="">Select a domain...</option>
                  {domains
                    .filter(d => !generateConfig.section_id || d.section_id === generateConfig.section_id)
                    .map(d => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                </select>
              </div>

              <div className="form-group">
                <label>Difficulty Level: *</label>
                <select
                  value={generateConfig.difficulty_level}
                  onChange={(e) => setGenerateConfig(prev => ({ ...prev, difficulty_level: e.target.value }))}
                  disabled={generating}
                >
                  <option value="foundation">Foundation</option>
                  <option value="developing">Developing</option>
                  <option value="proficient">Proficient</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>

              <div className="form-group">
                <label>Number of Questions: *</label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={generateConfig.question_count}
                  onChange={(e) => setGenerateConfig(prev => ({ ...prev, question_count: parseInt(e.target.value) || 1 }))}
                  disabled={generating}
                />
                <small>Generate 1-50 questions at a time</small>
              </div>

              <div className="info-box">
                <p><strong>Note:</strong> This will use AI to generate {generateConfig.question_count} new questions based on your selection. Questions will be added to the database immediately.</p>
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowGenerateModal(false)}
                className="btn-cancel"
                disabled={generating}
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateQuestions}
                className="btn-primary"
                disabled={generating}
              >
                {generating ? (
                  <>
                    <span className="spinner-small"></span>
                    Generating...
                  </>
                ) : (
                  <>✨ Generate {generateConfig.question_count} Questions</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SelectiveQuestionBank;
